'use client';

import React, { useEffect } from 'react';
import posthog from 'posthog-js';

/**
 * App Router root-layout error boundary.
 *
 * Catches uncaught React render errors that escape the nearest `error.tsx`
 * boundary. Required by Next.js App Router: when a render error reaches the
 * root layout, Next REPLACES the layout with this component, which means
 * `<PostHogProvider>` (mounted inside the root layout) is NOT in the tree
 * above us. We therefore have to initialize `posthog-js` inline before
 * capturing - relying on `PostHogProvider`'s init would silently drop the
 * event. Subsequent renders with the same singleton are no-ops thanks to
 * the `posthog.__loaded` guard.
 *
 * Client-side errors outside the render tree (event handlers, promises)
 * are captured by `posthog-js` autocapture when enabled in project settings.
 *
 * See GEN-1360 for context.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}): React.ReactElement {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!key) return;

    // Initialize posthog-js if it hasn't been initialized yet. global-error.tsx
    // replaces the root layout, so PostHogProvider may not have mounted.
    // `__loaded` is set internally by posthog-js after init() completes.
    const phWithLoaded = posthog as typeof posthog & { __loaded?: boolean };
    if (!phWithLoaded.__loaded) {
      posthog.init(key, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: false,
      });
    }

    posthog.captureException(error, {
      // Next stamps `digest` on errors thrown in Server Components so client
      // and server stack traces can be correlated in PostHog.
      digest: error.digest,
      source: 'global-error.tsx',
      // platform (HAL-234, Axis A): tag as first-party halcy.ai. global-error
      // inits its own posthog instance (PostHogProvider isn't mounted here), so
      // the super-property registered there doesn't apply — stamp it per-event.
      platform: 'landing',
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          fontFamily: 'system-ui, -apple-system, sans-serif',
          padding: '2rem',
          maxWidth: '600px',
          margin: '4rem auto',
          color: '#1f2937',
        }}
      >
        <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Something went wrong</h1>
        <p style={{ marginBottom: '1.5rem' }}>
          We&apos;ve been notified and are looking into it. You can try reloading the page.
        </p>
        <button
          type="button"
          onClick={() => reset()}
          style={{
            background: '#f59e0b',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontSize: '1rem',
          }}
        >
          Try again
        </button>
      </body>
    </html>
  );
}
